import type { ResolutionString } from "@public/charting_library";
import { TRADE_GREEN_HEX, TRADE_RED_HEX } from "../../../lib/trade-colors.ts";

export const DEFAULT_INTERVAL = "60" as ResolutionString;

export const CHART_FONT_FAMILY =
	"'Pretendard Variable', 'Pretendard', 'Apple SD Gothic Neo', 'Malgun Gothic', 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', sans-serif";

export const CHART_FAVORITE_INTERVALS = [
	"1",
	"5",
	"15",
	"60",
	"240",
	"1D",
] as ResolutionString[];

export const CHART_TIME_FRAMES = [
	{
		text: "5y",
		resolution: "1W" as ResolutionString,
		description: "5 Years",
	},
	{
		text: "1y",
		resolution: "1D" as ResolutionString,
		description: "1 Year",
	},
	{
		text: "6m",
		resolution: "1D" as ResolutionString,
		description: "6 Months",
	},
	{
		text: "3m",
		resolution: "240" as ResolutionString,
		description: "3 Months",
	},
	{
		text: "1m",
		resolution: "60" as ResolutionString,
		description: "1 Month",
	},
	{
		text: "5d",
		resolution: "30" as ResolutionString,
		description: "5 Days",
	},
	{
		text: "1d",
		resolution: "5" as ResolutionString,
		description: "1 Day",
	},
] as const;

const CHART_MOBILE_SCALE_FONT_SIZE = 10;
const CHART_DESKTOP_SCALE_FONT_SIZE = 12;

const commonOverrides = {
	"mainSeriesProperties.candleStyle.upColor": TRADE_GREEN_HEX,
	"mainSeriesProperties.candleStyle.downColor": TRADE_RED_HEX,
	"mainSeriesProperties.candleStyle.borderUpColor": TRADE_GREEN_HEX,
	"mainSeriesProperties.candleStyle.borderDownColor": TRADE_RED_HEX,
	"mainSeriesProperties.candleStyle.wickUpColor": TRADE_GREEN_HEX,
	"mainSeriesProperties.candleStyle.wickDownColor": TRADE_RED_HEX,
	"paneProperties.legendProperties.showLegend": false,
	"paneProperties.legendProperties.showBarChange": true,
	"paneProperties.legendProperties.showSeriesTitle": true,
	"paneProperties.legendProperties.showStudyTitles": true,
	"paneProperties.legendProperties.showStudyValues": true,
	"paneProperties.legendProperties.showVolume": true,
	volumePaneSize: "medium",
} as const;

export const chartThemeConfig = {
	light: {
		loadingScreen: {
			backgroundColor: "#ffffff",
			foregroundColor: TRADE_GREEN_HEX,
		},
		overrides: {
			...commonOverrides,
			"paneProperties.background": "#ffffff",
			"paneProperties.backgroundGradientStartColor": "#ffffff",
			"paneProperties.backgroundGradientEndColor": "#ffffff",
			"paneProperties.horzGridProperties.color": "#ffffff",
			"paneProperties.vertGridProperties.color": "#ffffff",
			"paneProperties.separatorColor": "rgba(107, 118, 124, 0.12)",
			"scalesProperties.textColor": "rgba(107, 118, 124, 0.7)",
			"scalesProperties.lineColor": "rgba(107, 118, 124, 0.12)",
		},
	},
	dark: {
		loadingScreen: {
			backgroundColor: "#101213",
			foregroundColor: TRADE_GREEN_HEX,
		},
		overrides: {
			...commonOverrides,
			"paneProperties.background": "#101213",
			"paneProperties.backgroundGradientStartColor": "#101213",
			"paneProperties.backgroundGradientEndColor": "#101213",
			"paneProperties.horzGridProperties.color": "#101213",
			"paneProperties.vertGridProperties.color": "#101213",
			"paneProperties.separatorColor": "rgba(137, 145, 150, 0.10)",
			"scalesProperties.textColor": "rgba(137, 145, 150, 0.72)",
			"scalesProperties.lineColor": "rgba(137, 145, 150, 0.10)",
		},
	},
} as const;

export function getChartOverrides(
	theme: keyof typeof chartThemeConfig,
	isMobile: boolean,
) {
	return {
		...chartThemeConfig[theme].overrides,
		"scalesProperties.fontSize": isMobile
			? CHART_MOBILE_SCALE_FONT_SIZE
			: CHART_DESKTOP_SCALE_FONT_SIZE,
	};
}
